import { createContext, useContext } from 'react';

/**
 * The business day as the Console's screens read it (IP-134, IP-129; design
 * 2026-10-06-order-review-business-day-design.md 8.1): the day a person
 * works in, whether reviews are paused, and a way to read it again.
 *
 *   day           GET /books/days's answer as BusinessDayProvider last read
 *                 it - { now, today, openDealingDay, reviewPaused, stages,
 *                 days } - or null before the first reading
 *   reviewPaused  that answer's reviewPaused - { paused, businessDate,
 *                 stage, stageLabel, since } - when a step pauses reviews,
 *                 else null: while a day's Repricing or Day end step runs,
 *                 ORDER_REVIEW is refused (REVIEW_PAUSED, 409) for orders
 *                 dated on or after that business date, system-wide (A-4).
 *                 The screens grey Review for those orders with the same
 *                 sentence; reject, send-back, approve and cancel are never
 *                 paused
 *   refresh()     read the day again - every order action calls it when it
 *                 finishes, because an approval or a reject can move the day
 *
 * The provider that owns the one read of GET /books/days (polling on the
 * idle monitor's heartbeat, only while the person is active) wraps the
 * Workspace in Console.jsx. Without it - a screen rendered on its own, a
 * test - every screen sees this default: no day, nothing paused, a refresh
 * that does nothing. Nothing is ever paused by the absence of a reading: the
 * server refuses a paused review in any case, in its own words.
 */
export const NO_BUSINESS_DAY = Object.freeze({ day: null, reviewPaused: null, refresh: () => {} });

export const BusinessDayContext = createContext(NO_BUSINESS_DAY);

/** The business day the Console is in: { day, reviewPaused, refresh }. */
export function useBusinessDay() {
    return useContext(BusinessDayContext) ?? NO_BUSINESS_DAY;
}
